import { useMemo, useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Card, Typography, Button } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ProjectInfoCard } from 'src/view/components/cardComponent';
import ProjectOverview from 'src/resources/images/Project_overview.png';
import Monitoring from 'src/resources/images/Monitoring.png';
import HumanImpact from 'src/resources/images/Human_impact.png';
import Richness from 'src/resources/images/SPECIES_RICHNESS.png';
import OverallHealth from 'src/resources/images/OVERALL_HEALTH.png';
import Biohealth from 'src/resources/images/Biodiversity_heath.png';
import { getHabitatHealthRangeCategory, getNDVIRangeCategory, getCPLandRangeCategory, getHDIRangeCategory } from 'src/constants/index';
import { convertToNumber, getTaxonomicDissimilarityRangeCategory } from 'src/constants/index';
import { getSpeciesAbundanceRangeCategory } from 'src/constants/index';
import { useAppState } from 'src/state-management/state-provider';
import SiteMapWithLayer from 'src/view/DMap/SiteMapWithLayer';
import SiteList from 'src/view/components/SiteList';
import { isSuccessBioAnalysis, roundToTwoDecimalPlaces } from 'src/util/util';
import { Site, UserRole, ProjectStatusEnum } from 'src/api/Api';
import { BIODIVERSITY_GLOBAL_LAYERS } from 'src/mapbox-helpers/global-datasets';
import { CustomRasterLayer } from 'src/mapbox-helpers/map-types';
import clientService from 'src/clientService';

import { SampleProjectId } from 'src/enum';

const BiodiversityDashboardSiteInfo = () => {
  const { state } = useAppState();
  const { biodiversityProjectAnalysis, projectDetails } = state.selectedProject;
  const { user } = state;
  const [insituSpeciesData, setInsituSpeciesData] = useState<any>(null);
  const [insituLoading, setInsituLoading] = useState(false);
  const isSampleProject = projectDetails?.project_id === SampleProjectId;
  

  const isUserRole = useMemo(() => {
    if (!user?.role || user.role.length === 0) return false;
    const roles = user.role;
    const hasUserRole = roles.includes(UserRole.User);
    const hasAdminRole = roles.includes(UserRole.Admin);
    const hasEmployeeRole = roles.includes(UserRole.Employee);
    return hasUserRole && !hasAdminRole && !hasEmployeeRole;
  }, [user]);
  const allowExporting = !(isSampleProject && isUserRole);
  const filterSpeciesData = biodiversityProjectAnalysis?.exsitu_species_distribution;

  // Check if species_distribution is empty or has no data
  const hasSpeciesDistributionData = useMemo(() => {
    if (!filterSpeciesData || Object.keys(filterSpeciesData).length === 0) {
      return false;
    }
    // Check if any of the threat level categories have data
    const hasData =
      (filterSpeciesData?.critically_endangered?.[0]?.total &&
        parseInt(filterSpeciesData.critically_endangered[0].total) > 0) ||
      (filterSpeciesData?.endangered?.[0]?.total &&
        parseInt(filterSpeciesData.endangered[0].total) > 0) ||
      (filterSpeciesData?.vulnerable?.[0]?.total &&
        parseInt(filterSpeciesData.vulnerable[0].total) > 0) ||
      (filterSpeciesData?.near_threatened?.[0]?.total &&
        parseInt(filterSpeciesData.near_threatened[0].total) > 0) ||
      (filterSpeciesData?.least_concerned?.[0]?.total &&
        parseInt(filterSpeciesData.least_concerned[0].total) > 0);
    return hasData;
  }, [filterSpeciesData]);

  const speciesData = [
    {
      name: 'Critically Endangered',
      value1: parseInt(filterSpeciesData?.critically_endangered?.[0]?.total || '0'),
      species: filterSpeciesData?.critically_endangered?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Endangered',
      value1: parseInt(filterSpeciesData?.endangered?.[0]?.total || '0'),
      species: filterSpeciesData?.endangered?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Vulnerable',
      value1: parseInt(filterSpeciesData?.vulnerable?.[0]?.total || '0'),
      species: filterSpeciesData?.vulnerable?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Near Threatened',
      value1: parseInt(filterSpeciesData?.near_threatened?.[0]?.total || '0'),
      species: filterSpeciesData?.near_threatened?.[0]?.species_list?.join(', ') || '',
    },
    {
      name: 'Least Concerned',
      value1: parseInt(filterSpeciesData?.least_concerned?.[0]?.total || '0'),
      species: filterSpeciesData?.least_concerned?.[0]?.species_list?.join(', ') || '',
    },
  ];
  console.log(biodiversityProjectAnalysis);
  useEffect(() => {
      try {
        Highcharts.setOptions({
          exporting: {
            enabled: allowExporting,
          },
        });
      } catch (e) {
        // Ignore if Highcharts isn't ready; this is defensive
        console.warn('Failed to set Highcharts exporting option', e);
      }
    }, [allowExporting]); 
  const chartData = speciesData
    .filter((d) => d.value1)
    .map((item) => ({
      name: item.name,
      y: item.value1,
      description: item.species
        .split(', ')
        // @ts-ignore
        .map((species: any) => `<br/>- ${species}`)
        .join(''),
    }));
  const options = useMemo(() => {
    return {
      chart: {
        type: 'pie',
      },
      // ensure per-chart exporting follows permission
      exporting: {
        enabled: allowExporting,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        pointFormat: `
                Total No.: <b>{point.y}</b><br/>
                Species: <b>{point.description}</b><br/>
                Percentage: <b>{point.percentage:.1f}%</b>
            `,
      },
      legend: {
        enabled: true, // Ensure the legend is enabled
        layout: 'horizontal', // Arrange legends vertically
        align: 'center', // Align legends to the right
        verticalAlign: 'bottom', // Vertically align legends to the middle
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            distance: -30,
            enabled: true,
            format: '<b>{point.y}</b><br/>',
            style: {
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 'bold',
            },
          },
          innerSize: '50%', // For donut effect
        },
      },
      series: [
        {
          name: 'Species',
          colorByPoint: true,
          showInLegend: true, // Enable legends for this series
          data: chartData, // Ensure this contains valid data
        },
      ],
    };
  }, [chartData, allowExporting]);

  // Fetch in-situ species distribution data for project
  useEffect(() => {
    if (projectDetails?.project_id) {
      setInsituLoading(true);
      clientService.api
        .readBiodiversityInsituSummaryApiV1ProjectsProjectIdBiodiversityInsituSummaryGet(
          projectDetails.project_id
        )
        .then((response) => {
          setInsituSpeciesData(response.data);
          setInsituLoading(false);
        })
        .catch((error: any) => {
          console.error('Error fetching in-situ species data:', error);
          setInsituSpeciesData(null);
          setInsituLoading(false);
        });
    } else {
      setInsituSpeciesData(null);
    }
  }, [projectDetails?.project_id]);

  // Process in-situ species distribution data
  const insituSpeciesDistributionData = useMemo(() => {
    if (!insituSpeciesData) return null;
    
    const insituData = [
      {
        name: 'Critically Endangered',
        value1: parseInt(insituSpeciesData?.critically_endangered?.[0]?.total || '0'),
        species: insituSpeciesData?.critically_endangered?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Endangered',
        value1: parseInt(insituSpeciesData?.endangered?.[0]?.total || '0'),
        species: insituSpeciesData?.endangered?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Vulnerable',
        value1: parseInt(insituSpeciesData?.vulnerable?.[0]?.total || '0'),
        species: insituSpeciesData?.vulnerable?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Near Threatened',
        value1: parseInt(insituSpeciesData?.near_threatened?.[0]?.total || '0'),
        species: insituSpeciesData?.near_threatened?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Least Concerned',
        value1: parseInt(insituSpeciesData?.least_concerned?.[0]?.total || '0'),
        species: insituSpeciesData?.least_concerned?.[0]?.species_list?.join(', ') || '',
      },
      {
        name: 'Unknown',
        value1: parseInt(insituSpeciesData?.unknown?.[0]?.total || '0'),
        species: insituSpeciesData?.unknown?.[0]?.species_list?.join(', ') || '',
      },
    ];

    return insituData
      .filter((d) => d.value1)
      .map((item) => ({
        name: item.name,
        y: item.value1,
        description: item.species
          .split(', ')
          .map((species: any) => `<br/>- ${species}`)
          .join(''),
      }));
  }, [insituSpeciesData]);

  const hasInsituData = useMemo(() => {
    return insituSpeciesDistributionData && insituSpeciesDistributionData.length > 0;
  }, [insituSpeciesDistributionData]);

  const insituSpeciesOptions = useMemo(() => {
    return {
      chart: {
        type: 'pie',
      },
      exporting: {
        enabled: allowExporting,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        pointFormat: `
                Total No.: <b>{point.y}</b><br/>
                Species: <b>{point.description}</b><br/>
                Percentage: <b>{point.percentage:.1f}%</b>
            `,
      },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            distance: -30,
            enabled: true,
            format: '<b>{point.y}</b><br/>',
            style: {
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 'bold',
            },
          },
          innerSize: '50%',
        },
      },
      series: [
        {
          name: 'Species',
          colorByPoint: true,
          showInLegend: true,
          data: insituSpeciesDistributionData || [],
        },
      ],
    };
  }, [insituSpeciesDistributionData, allowExporting]);

  return (
    <Box sx={{ mt: 5 }}>
      <Box className="bio-map" sx={{ mb: 3 }}></Box>

      {projectDetails?.sites?.length && (
        <Box className="bio-map" mb={3} height={600}>
          <SiteMapWithLayer
            sites={projectDetails?.sites?.filter(isSuccessBioAnalysis)}
            requiredBounrdaryMarker={true}
            fitToBoundary={true}
            rasterLayer={BIODIVERSITY_GLOBAL_LAYERS as CustomRasterLayer[]}
          ></SiteMapWithLayer>
        </Box>
      )}

      <Box
        sx={{
          border: '1px solid #ccc',
          backgroundColor: '#fff',
          marginTop: '5px',
          borderRadius: '8px',
          boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '22px', color: '#151E15', m: 2 }}>
          {projectDetails?.project_name}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #D9D9D9' }}
          gap={2}
        />
        <Grid container spacing={4}>
          <ProjectInfoCard
            imageSrc={ProjectOverview}
            title="Project Details"
            data={[
              { label: 'Total Area', value: `${roundToTwoDecimalPlaces(projectDetails?.total_area || projectDetails?.sites?.reduce((a: number, i: Site) => a + (i.area ? i.area : 0), 0))} ${projectDetails?.area_unit}` },
              { label: 'Total Sites', value: projectDetails?.sites?.length || 0 },
              // {
              //   label: 'Total Species Identified',
              //   value: biodiversityProjectAnalysis?.total_species_identified || 0,
              // },
            ]}
          />
          <ProjectInfoCard
          imageSrc={Monitoring}
          title="Monitoring & Data Collection"
          data={
            isUserRole && !isSampleProject && projectDetails?.project_status === ProjectStatusEnum.Draft
              ? [] // hide data for user
              : [
                  {
                    label: 'Cameras Devices Installed',
                    value: biodiversityProjectAnalysis?.cameras_installed || 0,
                  },
                  {
                    label: 'Audio Devices Installed',
                    value: biodiversityProjectAnalysis?.audio_devices_installed || 0,
                  },
                  {
                    label: 'Total Images/Videos',
                    value: biodiversityProjectAnalysis?.total_images_videos || 0,
                  },
                  {
                    label: 'Audio Files',
                    value: biodiversityProjectAnalysis?.total_audiofiles || 0,
                  },
                  {
                    label: 'Unique Species Count',
                    value: biodiversityProjectAnalysis?.insitu_species_count || 0,
                  },
                ]
          }
        >
          {/* 🔥 Add button inside the card so it appears right below heading */}
          {isUserRole && !isSampleProject && projectDetails?.project_status === ProjectStatusEnum.Draft && (
            <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() =>
                  window.open(
                    'https://calendly.com/jaya-kandir-darukaa/new-meeting',
                    '_blank'
                  )
                }
                sx={{
                  backgroundColor: '#4CAF50',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  '&:hover': { backgroundColor: '#45a049' },
                }}
              >
                Register Project
              </Button>
            </Box>
          )}
        </ProjectInfoCard>
          
        </Grid>
      </Box>
      <Box
        sx={{
          overflowY: 'auto',
          border: '1px solid #ccc',
          padding: '2px',
          backgroundColor: '#fff',
          margin: '10px',
          borderRadius: '8px',
          boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
        }}
      >
        <Grid container>
            <ProjectInfoCard
              imageSrc={OverallHealth}
              title="Ecosystem Health"
              data={[
                {
                  label: 'Habitat Health Index',
                  value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.habitat_health || 0),
                  tooltip:
                    'Assesses the condition and sustainability of habitats for supporting biodiversity.',
                  color: getHabitatHealthRangeCategory(
                    convertToNumber(biodiversityProjectAnalysis?.habitat_health ?? 0),
                  ).color,
                },
                {
                label: 'Normalized Difference Vegetation Index',
                value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.mean_ndvi || 0),
                tooltip:
                  'Standardized numerical indicator that quantifies the health and density of live green vegetation.',
                color: getNDVIRangeCategory(
                  convertToNumber(biodiversityProjectAnalysis?.mean_ndvi ?? 0),
                ).color,
              },
              {
                label: 'Landscape Fragmentation Index',
                value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.cp_land || 0),
                tooltip:
                  'Measures the structural integrity of a landscape by assessing the degree to which natural habitats are broken into smaller, isolated patches.',
                color: getCPLandRangeCategory(
                  convertToNumber(biodiversityProjectAnalysis?.cp_land ?? 0),
                ).color,
              }
              ]}
            />
          <ProjectInfoCard
            imageSrc={Richness}
            title="Species Assemblage"
            data={[
              {
                label: 'Species Diversity Index',
                value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.species_diversity || 0),
                tooltip:
                  'Reflects the variety of species within a specific area, accounting for their abundance.',
                color: getSpeciesAbundanceRangeCategory(
                  convertToNumber(biodiversityProjectAnalysis?.species_diversity ?? 0),
                ).color,
              },
              {
                label: 'Species Richness Index',
                value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.species_richness || 0),
                tooltip:
                  'Represents the total number of different species in a given area, without considering their abundance.',
                color: getSpeciesAbundanceRangeCategory(
                  convertToNumber(biodiversityProjectAnalysis?.species_richness ?? 0),
                ).color,
              },
              {
                label: 'Taxonomic Dissimilarity',
                value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.taxonomic_dissimilarity || 0),
                tooltip:
                  'Measures the evolutionary or taxonomic differences between species within an ecosystem.',
                color: getTaxonomicDissimilarityRangeCategory(
                  convertToNumber(biodiversityProjectAnalysis?.taxonomic_dissimilarity ?? 0),
                ).color,
              },
            ]}
          />
          <ProjectInfoCard
            imageSrc={Biohealth}
            title="Species Status"
            data={[
              //{
                //label: 'Biodiversity Intactness Index',
                //value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.biodiversity_intactness || 0),
                //tooltip:
                //  'A measure of how much of the original biodiversity remains intact in the ecosystem, relative to undisturbed conditions.',
                //color: getIntactnessRangeCategory(
                 // convertToNumber(biodiversityProjectAnalysis?.biodiversity_intactness ?? 0),
               // ).color,
             // },
              {
                label: 'Mean Species Abundance',
                value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.mean_species_abundance || 0),
                tooltip:
                  'The average abundance of native species in the area compared to their abundance in an undisturbed ecosystem.',
                color: getSpeciesAbundanceRangeCategory(
                  convertToNumber(biodiversityProjectAnalysis?.mean_species_abundance ?? 0),
                ).color,
              },
            ]}
          />

          <ProjectInfoCard
            imageSrc={HumanImpact}
            title="Threats"
            data={[
              //{
                //label: 'Potential Disappearance Fraction',
                //value: roundToTwoDecimalPlaces(biodiversityProjectAnalysis?.potential_disappearance_fraction || 0),
                //tooltip:
                 // 'The estimated proportion of species at risk of disappearing due to environmental degradation or human impact.',
               // color: getSpeciesAbundanceRangeCategory(
                  //convertToNumber(
                   // biodiversityProjectAnalysis?.potential_disappearance_fraction ?? 0,
                  //),
               // ).color,
             // },
              {
                label: 'Human Disturbance Index',
                value: roundToTwoDecimalPlaces(
                  biodiversityProjectAnalysis?.hdi || 0,
                ),
                tooltip:
                  'A normalized proximity metric that quantifies the ecological "buffer" between a specific site and the nearest urban built-up area.',
                color: getHDIRangeCategory(
                  convertToNumber(
                    biodiversityProjectAnalysis?.hdi ?? 0,
                  ),
                ).color,
              }
            ]}
          />
        </Grid>
      </Box>
      <Grid container spacing={3} sx={{ marginTop: '10px' }}>
        {/* Ex-Situ Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            <Typography
              component="div"
              sx={{
                textAlign: 'left',
                fontSize: '16px',
                fontWeight: 600,
                color: '#151E15',
                p: '16px',
              }}
            >
              {'Species Distribution by Threat Level (Ex-Situ)'}
            </Typography>
            <Box className="divider" sx={{ p: 2 }} />
            {hasSpeciesDistributionData && chartData.length > 0 ? (
              <HighchartsReact highcharts={Highcharts} options={options} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                  }}
                >
                  No Ex-situ data found
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* In-Situ Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            <Typography
              component="div"
              sx={{
                textAlign: 'left',
                fontSize: '16px',
                fontWeight: 600,
                color: '#151E15',
                p: '16px',
              }}
            >
              {'Species Distribution by Threat Level (In-Situ)'}
            </Typography>
            <Box className="divider" sx={{ p: 2 }} />
            {insituLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                  }}
                >
                  Loading...
                </Typography>
              </Box>
            ) : hasInsituData ? (
              <HighchartsReact highcharts={Highcharts} options={insituSpeciesOptions} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                  }}
                >
                  No In-situ data found
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
      <SiteList type={'bio-diversity'}></SiteList>
    </Box>
  );
};

export default BiodiversityDashboardSiteInfo;
